import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, switchMap, take, throwError } from 'rxjs';
import { DialogCloseResult, DialogRef, DialogResult, DialogService } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import { EI_DIALOG_MESSAGES } from '../modules/ei-transition/constants/ei-dialog-messages';
import { DocumentService } from './document.service';
import { IAppConfig } from '../shared/interfaces/app-config';
import { AppConfigService } from './app-config.service';
import { EiAddStudent as EiStudentResponse } from '../modules/ei-transition/models/ei.model';
import { dialogAnimationDuration, dialogTimer } from '../shared/constants/dialog-specs';
import { EiSubmitStudent } from '../modules/ei-transition/models/ei-submit-student.model';
import { ApiResponse } from '../shared/interfaces/api-response';
import { CheckEI } from '../modules/ei-transition/interfaces/check-ei';
import { EIChildStatus } from '../modules/ei-transition/interfaces/request-new-document';
import { EIAutoEligibleCondition } from '../modules/ei-transition/interfaces/ei-auto-eligible-condition';

@Injectable({
  providedIn: 'root',
})
export class EITransitionService {
  private readonly firstResult: number = 1;
  config: IAppConfig;
  headers: HttpHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    accept: '*/*',
  });

  httpOptions: { headers: HttpHeaders } = {
    headers: this.headers,
  };

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private readonly dialogs: DialogService,
    private readonly documents: DocumentService,
    private readonly notifications: NotificationService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  // TODO: Need to bring this up to Uday, the return type is not consistent from the backend. Following two different patterns.
  // Ideally, what should be returned is `ApiResponse<CheckEI>`
  checkEiId(eiId: string, agencyId: string): Observable<CheckEI> {
    return this.http
      .get<CheckEI>(
        `${this.config.apiUrl}/EITransition/checkEiIdExists/${eiId}/${agencyId}`
      )
      .pipe(catchError(this.handleError));
  }

  public CreateEI(eiData: EiSubmitStudent): Observable<ApiResponse<string>> {
    return this.http
      .post<ApiResponse<string>>(
        `${this.config.apiUrl}/EITransition`,
        eiData,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  public GetEI(studentSk: string): Observable<EiStudentResponse> {
    return this.http
      .get<EiStudentResponse>(
        `${this.config.apiUrl}/EITransition/EI/${studentSk}`,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  public DeleteEI(studentSk: string): Observable<ApiResponse<string>> {
    return this.http
      .delete<ApiResponse<string>>(
        `${this.config.apiUrl}/EITransition/EI/${studentSk}`,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  confirmDeleteDraft(studentSk: string): Observable<boolean> {
    const dialog: DialogRef = this.dialogs.open({
      title: EI_DIALOG_MESSAGES.deleteDraftTitle,
      content: EI_DIALOG_MESSAGES.deleteWarning,
      cssClass: 'ei-delete-draft-dialog',
      actions: [
        { text: 'No', fillMode: 'outline' },
        { text: 'Yes', themeColor: 'error' },
      ],
      actionsLayout: 'stretched',
      width: 550,
      maxWidth: 'calc(100vw - 32px)',
    });

    return dialog.result.pipe(
      take(this.firstResult),
      switchMap((result: DialogResult) => {
        if (result instanceof DialogCloseResult || result.text !== 'Yes') {
          return of(false);
        }
        // Recheck the current record before deleting a potentially stale grid row.
        return this.GetEI(studentSk).pipe(
          switchMap((record: EiStudentResponse) => {
            if (!record.draftRecord) {
              this.notifyDraftDeletion(false, 'This record is no longer a draft. Refresh the grid and try again.');
              return of(false);
            }
            if (!record.childEIId) {
              this.notifyDraftDeletion(false);
              return of(false);
            }
            return this.documents.deleteDocumentsById(record.childEIId, 'EI Draft Deleted', studentSk).pipe(
              switchMap((deleted: boolean) => deleted
                ? this.DeleteEI(studentSk)
                : of({ isRequestSucessful: false } as ApiResponse<string>)),
              map((response: ApiResponse<string>) => {
                const success: boolean = response.isRequestSucessful === true;
                this.notifyDraftDeletion(success);
                return success;
              })
            );
          })
        );
      }),
      catchError(() => {
        this.notifyDraftDeletion(false);
        return of(false);
      })
    );
  }

  private notifyDraftDeletion(success: boolean, message?: string): void {
    this.notifications.show({
      content: message ?? (success ? 'Draft deleted successfully.' : 'Unable to delete the draft. Please try again.'),
      type: { style: success ? 'success' : 'error', icon: true },
      hideAfter: dialogTimer,
      animation: { type: 'slide', duration: dialogAnimationDuration },
      position: { horizontal: 'center', vertical: 'top' },
      closable: false,
    });
  }

  public requestNewDocument(
    requestNewDocumentData: EIChildStatus
  ): Observable<ApiResponse<EIChildStatus>> {
    return this.http
      .put<ApiResponse<EIChildStatus>>(
        `${this.config.apiUrl}/EITransition/UpdateChildStatus`,
        requestNewDocumentData
      )
      .pipe(catchError(this.handleError));
  }

  getEIAutoEligibleConditions(): Observable<EIAutoEligibleCondition[]> {
    return this.http
      .get<EIAutoEligibleCondition[]>(
        `${this.config.apiUrl}/EITransition/GetEiAeConditions`,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  // Error handling
  handleError(error: any): Observable<never> {
    let errorMessage: string = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}
